import React from 'react';
import PasswordField from '../PasswordField';

const { t } = require('../../i18n');

// Locale-aware pitch copy (resolved from the browser on each call).
export const getTrustLine = () => t('login.trustLine');
export const getPitchTitle = () => t('login.pitchTitle');
export const getPitchBody = () => t('login.pitchBody');

export const LoginError = ({ error, emailOnly, onBack }) => {
  if (!error) return null;

  switch(error) {
    case 'form required':
      return (
        <div className="aud-banner aud-banner-error text-danger">
          <p>{emailOnly ? t('login.emailRequired') : t('login.emailAndPasswordRequired')}</p>
        </div>
      );
    case 'User not found':
      return <div className="aud-banner aud-banner-error text-danger"><p>{t('login.emailNotFound')}</p></div>;
    case 'Invalid password':
      return <div className="aud-banner aud-banner-error text-danger"><p>{t('login.passwordIncorrect')}</p></div>;
    case 'SSO sign-in failed':
      return <div className="aud-banner aud-banner-error text-danger"><p>{t('login.ssoUnavailable')}</p></div>;
    case 'popup blocked':
      return <div className="aud-banner aud-banner-info"><p className="mb-0">{t('login.popupBlocked')}</p></div>;
    default:
      return (
        <div className="aud-banner aud-banner-error text-danger">
          <p>
            Unfortunately we could not log you in. Please reach out to{' '}
            <a href="mailto:support@audirie.com">support@audirie.com</a>
            {' '}if the problem persists.
          </p>
          {onBack && (
            <button className="aud-link-btn" type="button" onClick={onBack}>
              {t('login.backToLogin')}
            </button>
          )}
        </div>
      );
  }
};

const Spinner = () => <div className="aud-spinner" role="status" aria-label="Loading" />;

/**
 * Two-pane shell. The sidebar shows the organisation once we know who the
 * user is, and the product pitch until then.
 */
export const LoginCard = ({ org, children }) => (
  <div className="aud-card" id="LoginForm">
    <div className="aud-panel" aria-hidden="true">
      <img src="/static/images/audirieLogo.png" alt="Audirie" className="aud-panel-logo" />
      {org ? (
        <div className="aud-panel-body">
          <div className="aud-org-badge">{(org.name || 'A').charAt(0).toUpperCase()}</div>
          <p className="aud-panel-title">{t('login.orgOnAudirie', { values: { name: org.name } })}</p>
          <p className="aud-panel-copy">{org.blurb || t('login.orgManagedSignIn')}</p>
        </div>
      ) : (
        <div className="aud-panel-body">
          <p className="aud-panel-title">{getPitchTitle()}</p>
          <p className="aud-panel-copy">{getPitchBody()}</p>
        </div>
      )}
      <span className="aud-trust">{getTrustLine()}</span>
    </div>
    <div className="aud-main">{children}</div>
  </div>
);

/**
 * Hard maintenance — replaces the login form while Connect reports
 * maintenance.enabled from the ops-portal poller cache.
 */
export const MaintenanceStep = ({ message, checking, onCheckAgain }) => (
  <div className="aud-step" data-testid="login-maintenance">
    <div>
      <h2 className="aud-heading">{t('login.maintenanceHeading')}</h2>
      <p className="aud-subline" style={{ marginTop: 8 }}>
        {message || t('login.maintenanceDefaultBody')}
      </p>
    </div>
    <div className="aud-banner aud-banner-info">
      <p className="mb-0">{t('login.maintenanceHint')}</p>
    </div>
    <button
      type="button"
      className="aud-btn-primary"
      disabled={checking}
      onClick={onCheckAgain}
    >
      {checking ? t('login.oneMoment') : t('login.checkAgain')}
    </button>
  </div>
);

/** Soft Core outage — form stays available but sign-in is discouraged. */
export const SoftOutageBanner = () => (
  <div className="aud-banner aud-banner-info" data-testid="login-soft-outage">
    <p className="mb-0">{t('login.softOutageBody')}</p>
  </div>
);

export const EmailLoginForm = ({ login, error, saving, seenBefore, onSubmit, onInputChange, onBack, disabled }) => (
  <form className="aud-step" action="#" onSubmit={onSubmit}>
    <div>
      <h2 className="aud-heading">{seenBefore ? t('login.welcomeBack') : t('login.findYourWayIn')}</h2>
      <p className="aud-subline" style={{ marginTop: 8 }}>
        {seenBefore ? t('login.startWithWorkEmail') : t('login.workEmailTellsUs')}
      </p>
    </div>
    <LoginError error={error} emailOnly onBack={onBack} />
    <div className="aud-field">
      <label htmlFor="email-input">{t('login.workEmail')}</label>
      <input
        id="email-input"
        className="form-control"
        name="email"
        type="email"
        autoComplete="username"
        placeholder="name@example.com"
        value={login.email}
        onChange={onInputChange}
        autoFocus
        disabled={disabled}
      />
    </div>
    <button disabled={saving || disabled} className="aud-btn-primary" type="submit">{t('login.continue')}</button>
  </form>
);

export const ContinueAsStep = ({ hint, onContinue, onDifferent }) => (
  <div className="aud-step">
    <div>
      <h2 className="aud-heading">{t('login.welcomeBack')}</h2>
      <p className="aud-subline" style={{ marginTop: 8 }}>{t('login.pickUpWhereLeftOff')}</p>
    </div>
    <div className="aud-options">
      <button type="button" className="aud-option aud-option-primary" onClick={onContinue}>
        <span className="aud-option-title">{t('login.continueAs', { values: { email: hint.email } })}</span>
        <span className="aud-option-detail">
          {hint.method === 'sso'
            ? (hint.orgName
              ? t('login.singleSignOnWithOrg', { values: { orgName: hint.orgName } })
              : t('login.singleSignOn'))
            : t('login.audiriePassword')}
        </span>
      </button>
    </div>
    <button type="button" className="aud-link-btn aud-quiet" onClick={onDifferent}>
      {t('login.useDifferentAccount')}
    </button>
  </div>
);

export const DetectingStep = () => (
  <div className="aud-step" aria-live="polite">
    <Spinner />
    <h2 className="aud-heading">{t('login.oneMoment')}</h2>
    <p className="aud-subline">{t('login.checkingOrganisationSignIn')}</p>
  </div>
);

export const OrgPickerStep = ({ email, providers, onPick, onBack }) => (
  <div className="aud-step">
    <div>
      <h2 className="aud-heading">{t('login.whichOrgHeading')}</h2>
      <p className="aud-subline" style={{ marginTop: 8 }}>
        {t('login.belongsToMultipleOrgs', { values: { email } })}
      </p>
    </div>
    <div className="aud-options">
      {providers.map((provider) => (
        <button
          key={provider.providerId}
          type="button"
          className="aud-option"
          onClick={() => onPick(provider)}
        >
          <span className="aud-option-title">{provider.orgName || t('login.yourOrganisation')}</span>
          <span className="aud-option-detail">{t('login.singleSignOn')}</span>
        </button>
      ))}
    </div>
    <button type="button" className="aud-link-btn aud-quiet" onClick={onBack}>
      {t('login.useDifferentEmail')}
    </button>
  </div>
);

export const SsoRedirect = ({ org, error, onBack }) => (
  <div className="aud-step" aria-live="polite">
    <LoginError error={error} onBack={onBack} />
    {!error && (
      <React.Fragment>
        <h2 className="aud-heading">
          {org?.name
            ? t('login.orgTakesItFromHere', { values: { name: org.name } })
            : t('login.yourOrgTakesItFromHere')}
        </h2>
        <p className="aud-subline">{t('login.openingTheirSignIn')}</p>
        <div className="aud-progress"><div /></div>
        <button type="button" className="aud-link-btn aud-quiet" onClick={onBack}>
          {t('login.notYouStartAgain')}
        </button>
      </React.Fragment>
    )}
  </div>
);

export const ReturningStep = () => (
  <div className="aud-step" aria-live="polite">
    <Spinner />
    <h2 className="aud-heading">{t('login.welcomeBack')}</h2>
    <p className="aud-subline">{t('login.verifiedGettingReady')}</p>
  </div>
);

export const SsoAccountBlocked = ({ org, message, onGoBack }) => (
  <div className="aud-step">
    <h2 className="aud-heading">
      {org?.name
        ? t('login.orgSignInComplete', { values: { name: org.name } })
        : t('login.accountNotReady')}
    </h2>
    <p className="aud-subline">{t('login.ssoWorkedButNeedsSetup')}</p>
    <div className="aud-banner aud-banner-error">
      <p>{message || t('login.noRoleAssigned')}</p>
    </div>
    <button className="aud-btn-primary" type="button" onClick={onGoBack}>
      {t('login.backToLogin')}
    </button>
  </div>
);

export const SsoFailure = ({ org, onGoBack }) => (
  <div className="aud-step">
    <h2 className="aud-heading">
      {org?.name
        ? t('login.orgDidntLetUsIn', { values: { name: org.name } })
        : t('login.couldntCompleteSso')}
    </h2>
    <p className="aud-subline">{t('login.orgSignInDeclined')}</p>
    <div className="aud-banner aud-banner-error">
      <p>
        If this keeps happening, send a note to your IT team — and let us know at{' '}
        <a href="mailto:support@audirie.com">support@audirie.com</a>
        {' '}so we can help.
      </p>
    </div>
    <button className="aud-btn-primary" type="button" onClick={onGoBack}>
      {t('login.tryAgain')}
    </button>
  </div>
);

export const SsoEmailMismatch = ({ org, onGoBack }) => (
  <div className="aud-step">
    <h2 className="aud-heading">
      {org?.name
        ? t('login.orgSignedInAsSomeoneElse', { values: { name: org.name } })
        : t('login.differentAccountSignedIn')}
    </h2>
    <p className="aud-subline">{t('login.orgSignOnConnectedElsewhere')}</p>
    <div className="aud-banner aud-banner-error">
      <p>{t('login.signOutCorporateFirst')}</p>
    </div>
    <button className="aud-btn-primary" type="button" onClick={onGoBack}>
      {t('login.backToLogin')}
    </button>
  </div>
);

export const PasswordLoginForm = ({
  login,
  error,
  saving,
  onSubmit,
  onInputChange,
  onBack,
  onForgotPassword
}) => (
  <form className="aud-step" action="#" onSubmit={onSubmit}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <h2 className="aud-heading">{t('login.enterPassword')}</h2>
      <button type="button" className="aud-identifier" onClick={onBack}>
        {login.email} · {t('login.change')}
      </button>
    </div>
    <LoginError error={error} />
    <div className="aud-field">
      <PasswordField
        id="password-input"
        label={t('login.password')}
        name="password"
        passwordValue={login.password}
        updateInput={onInputChange}
        autoFocus
      />
      <a
        className="aud-link-btn"
        href="#"
        onClick={(e) => { e.preventDefault(); onForgotPassword(); }}
      >
        {t('login.forgotPassword')}
      </a>
    </div>
    <button disabled={saving} className="aud-btn-primary" type="submit">{t('login.signIn')}</button>
  </form>
);

export const PasswordResetForm = ({
  email,
  result,
  saving,
  onSubmit,
  onInputChange,
  onCancel
}) => {
  let message;
  switch(result) {
    case 'sent':
      message = <p className="text-success">{t('login.resetEmailSent')}</p>;
      break;
    case 'need email':
      message = <div className="aud-banner aud-banner-error"><p className="text-danger">{t('login.needValidEmail')}</p></div>;
      break;
    case 'user not found':
      message = (
        <div className="aud-banner aud-banner-error">
          <p className="text-danger">
            You must register with Audirie before you can complete this task. If you do not have a registration link, contact us at{' '}
            <a href="mailto:support@audirie.com">support@audirie.com</a>
          </p>
        </div>
      );
      break;
    default:
      message = result ? (
        <div className="aud-banner aud-banner-error">
          <p className="text-danger">
            We could not reset your password. Please try again. If this persists please contact us at{' '}
            <a href="mailto:support@audirie.com">support@audirie.com</a>
          </p>
        </div>
      ) : null;
  }

  return (
    <form className="aud-step" action="#" method="POST" onSubmit={onSubmit} noValidate>
      <div>
        <h2 className="aud-heading">{t('login.resetPassword')}</h2>
        <p className="aud-subline" style={{ marginTop: 8 }}>{t('login.wellEmailLink')}</p>
      </div>
      {message}
      <div className="aud-field">
        <label htmlFor="email-input">{t('login.workEmail')}</label>
        <input
          id="email-input"
          className="form-control"
          name="email"
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={onInputChange} />
      </div>
      <button disabled={saving} className="aud-btn-primary" type="submit">{t('login.send')}</button>
      <a
        className="aud-link-btn aud-quiet"
        href="#"
        onClick={(e) => { e.preventDefault(); onCancel(); }}
      >
        {t('login.cancel')}
      </a>
    </form>
  );
};
